import { compact } from "lodash";
import { useMemo, useState } from "react";
import { ComplianceProgramCalibrationGroupFragment } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import Modal from "@components/modal/modal";

import CalibrationGroupForm from "./calibration-group-form";
import CalibrationGroupsList from "./calibration-groups-list";
import { CalibrationGroupsModalView, UsersByCalibrationGroup } from "./types";

const CalibrationGroupsModal = ({
  complianceProgramId,
  calibrationGroups,
  isCreatingNew,
  filteredUsers,
  onClose,
}: {
  complianceProgramId: number;
  calibrationGroups: ComplianceProgramCalibrationGroupFragment[];
  isCreatingNew: boolean;
  filteredUsers: BasicUser[];
  onClose: () => void;
}) => {
  const [view, setView] = useState(
    isCreatingNew
      ? CalibrationGroupsModalView.FORM
      : CalibrationGroupsModalView.LIST,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  const selectedGroup = useMemo(
    () => calibrationGroups.find(({ id }) => id === selectedGroupId) ?? null,
    [calibrationGroups, selectedGroupId],
  );
  const usersByCalibrationGroup: UsersByCalibrationGroup = useMemo(() => {
    const filteredGroups = selectedGroupId
      ? calibrationGroups.filter(({ id }) => id !== selectedGroupId)
      : calibrationGroups;

    return filteredGroups.reduce((acc, group) => {
      return {
        ...acc,
        [group.id]: {
          name: group.name,
          users: compact(group.users),
        },
      };
    }, {} as UsersByCalibrationGroup);
  }, [calibrationGroups, selectedGroupId]);

  return (
    <Modal onClose={onClose} topMargin={false}>
      {view === CalibrationGroupsModalView.LIST && (
        <CalibrationGroupsList
          calibrationGroups={calibrationGroups}
          onClose={onClose}
          onEdit={(groupId) => {
            setSelectedGroupId(groupId);
            setView(CalibrationGroupsModalView.FORM);
          }}
        />
      )}
      {view === CalibrationGroupsModalView.FORM && (
        <CalibrationGroupForm
          complianceProgramId={complianceProgramId}
          calibrationGroup={selectedGroup}
          usersByCalibrationGroup={usersByCalibrationGroup}
          users={filteredUsers}
          onClose={onClose}
          onCancel={() => {
            setSelectedGroupId(null);
            onClose();
          }}
        />
      )}
    </Modal>
  );
};

export default CalibrationGroupsModal;
